import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import * as authApi from "../api/auth";
import { setAccessToken, setOnAuthUpdate } from "../api/http";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
	const [access, setAccess] = useState(null);
	const [roles, setRoles] = useState([]);
	const [activeRole, setActiveRole] = useState(null);
	const [bootstrapping, setBootstrapping] = useState(true);
	const [user, setUser] = useState(null);

	const loadMe = useCallback(async () => {
		try {
			const me = await authApi.me();
			const serverTimeZone =
				typeof me.server_time_zone === "string"
					? me.server_time_zone.trim()
					: "";

			if (serverTimeZone) {
				localStorage.setItem("somnus_server_tz", serverTimeZone);
			} else {
				localStorage.removeItem("somnus_server_tz");
			}

			const fullName =
				me.full_name ||
				`${me.first_name || ""} ${me.last_name || ""}`.trim() ||
				me.username ||
				"Usuario";

			setUser({
				id: me.id ?? null,
				username: me.username || null,
				email: me.email || null,
				firstName: me.first_name || null,
				lastName: me.last_name || null,
				fullName,
				role: me.role || null,
				roles: Array.isArray(me.roles) ? me.roles : [],
				mustChangePassword: Boolean(me.must_change_password),
				passwordRequirements: Array.isArray(me.password_requirements)
					? me.password_requirements
					: [],
			});
			setRoles(Array.isArray(me.roles) ? me.roles : []);
		} catch {
			setUser(null);
			setRoles([]);
			localStorage.removeItem("somnus_server_tz");
		}
	}, []);

	// Conecta interceptor -> context
	useEffect(() => {
		setOnAuthUpdate((newAccess, newActiveRole) => {
			setAccess(newAccess);
			setActiveRole(newActiveRole ?? null);

			if (!newAccess) {
				setRoles([]);
				setUser(null);
				localStorage.removeItem("somnus_server_tz");
			}
		});

		return () => setOnAuthUpdate(null);
	}, []);

	// Cada vez que cambia el access, actualizamos el token del http client
	useEffect(() => {
		setAccessToken(access);
	}, [access]);

	// Rehidratar sesión al cargar la app (si existe cookie refresh_token)
	useEffect(() => {
		(async () => {
			try {
				const data = await authApi.refresh();

				setAccess(data.access);
				setAccessToken(data.access); // inmediato

				setActiveRole(data.active_role || null);

				await loadMe();
			} catch {
				setAccess(null);
				setAccessToken(null);
				setRoles([]);
				setActiveRole(null);
				setUser(null);
			} finally {
				setBootstrapping(false);
			}
		})();
	}, [loadMe]);

	const login = useCallback(
		async (username, password, act_as, keep_session) => {
			const data = await authApi.login({
				username,
				password,
				act_as,
				keep_session,
			});

			setAccess(data.access);
			setAccessToken(data.access); // inmediato

			setRoles(data.roles || []);
			setActiveRole(data.active_role || act_as || null);

			await loadMe();
		},
		[loadMe],
	);

	const switchRole = useCallback(async (actAs) => {
		const data = await authApi.switchRole(actAs);

		setAccess(data.access);
		setAccessToken(data.access);

		setRoles(data.roles || []);
		setActiveRole(data.active_role || data.activeRole || actAs);
	}, []);

	const logout = useCallback(async () => {
		try {
			await authApi.logout();
		} finally {
			setAccess(null);
			setAccessToken(null);
			setRoles([]);
			setActiveRole(null);
			setUser(null);
			localStorage.removeItem("somnus_server_tz");
		}
	}, []);

	const refreshUser = useCallback(async () => {
		await loadMe();
	}, [loadMe]);

	const value = useMemo(
		() => ({
			access,
			roles,
			activeRole,
			user,
			bootstrapping,
			login,
			switchRole,
			logout,
			refreshUser,
		}),
		[
			access,
			roles,
			activeRole,
			user,
			bootstrapping,
			login,
			switchRole,
			logout,
			refreshUser,
		],
	);

	return (
		<AuthContext.Provider value={value}>{children}</AuthContext.Provider>
	);
}

export function useAuth() {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
	return ctx;
}
